import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import api from "../../services/api";
import authService from "../../services/authService";
import Badge from "../../components/ui/badge/Badge";
import ComponentCard from "../../components/common/ComponentCard";
import ViewProductOffersTable from "../../components/tables/Products/ViewProductOffersTable";

interface Product {
    id: string;
    name: string;
    productId: string;
    imageUrl: string;
    description: string;
    defaultPrice: number;
    offer: {
        price: number;
        currency: string;
    }[];
    type: string;
    status: string;
    currency: string;
}

export default function ViewProduct() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchProduct = async () => {
            if (!id) return;

            try {
                setLoading(true);
                setError(null);
                const response = await api.get(`/api/products/${id}`, {
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.data && response.data.product) {
                    setProduct(response.data.product);
                }
            } catch (err: any) {
                console.error("Erro ao buscar produto:", err);
                setError(err.message || "Erro ao carregar produto");
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [id]);

    const handleGoBack = () => {
        navigate("/products?tab=mycoproductions");
    };

    const getTypeLabel = (type: string) => {
        const types: Record<string, string> = {
            'course': 'Curso',
            'subscription': 'Assinatura',
            'ebook': 'Ebook',
            'consulting': 'Consultoria',
            'mentoring': 'Mentoria',
            'other': 'Outro'
        };
        return types[type] || type;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'draft': return 'info';
            case 'inactive': return 'error';
            default: return 'error';
        }
    };

    const getStatusLabel = (status: string) => {
        const statuses: Record<string, string> = {
            'active': 'Ativo',
            'pending': 'Pendente',
            'inactive': 'Inativo',
            'draft': 'Rascunho'
        };
        return statuses[status] || status;
    };

    const formatCurrency = (value: number, currency: string = 'BRL') => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: currency
        }).format(value);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 text-center text-red-500">
                Erro: {error}
            </div>
        );
    }

    if (!product) {
        return (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                Produto não encontrado
            </div>
        );
    }

    return (
        <>
            <PageMeta
                title={`Sellyo - ${product.name}`}
                description="Visualizar produto"
            />
            <PageBreadcrumb
                pageTitle="Visualizar Produto"
                pageDescription="Você está visualizando este produto como co-produtor"
                showBackButton={true}
                onBack={handleGoBack}
            />

            <div className="grid grid-cols-12 gap-6">
                {/* Informações do Produto */}
                <div className="col-span-12">
                    <ComponentCard title="Informações do Produto">
                        <div className="flex flex-col md:flex-row gap-6">
                            {/* Imagem do produto */}
                            <div className="w-full md:w-48 flex-shrink-0">
                                <div className="w-full aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                    {product.imageUrl ? (
                                        <img
                                            src={product.imageUrl}
                                            alt={product.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <span className="text-gray-400 text-sm">Sem imagem</span>
                                    )}
                                </div>
                            </div>

                            {/* Detalhes do produto */}
                            <div className="flex-1 space-y-4">
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                                        {product.name}
                                    </h2>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        ID: {product.productId}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">Tipo</span>
                                        <span className="text-gray-800 dark:text-white font-medium">
                                            {getTypeLabel(product.type)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">Preço Base</span>
                                        <span className="text-gray-800 dark:text-white font-medium">
                                            {formatCurrency(product.offer?.[0].price, product.currency)}
                                        </span>
                                    </div>
                                    <div>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400">Status</span>
                                        <Badge size="sm" color={getStatusColor(product.status)}>
                                            {getStatusLabel(product.status)}
                                        </Badge>
                                    </div>
                                </div>

                                {product.description && (
                                    <div>
                                        <span className="block text-sm text-gray-500 dark:text-gray-400 mb-1">Descrição</span>
                                        <p className="text-gray-700 dark:text-gray-300">
                                            {product.description}
                                        </p>
                                    </div>
                                )}

                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        <strong>Modo de visualização:</strong> Como co-produtor, você pode visualizar as informações do produto e copiar os links de venda, mas não pode fazer alterações.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </ComponentCard>
                </div>

                {/* Tabela de Ofertas */}
                <div className="col-span-12">
                    <ComponentCard title="Ofertas e Links de Venda">
                        <ViewProductOffersTable productId={id} />
                    </ComponentCard>
                </div>
            </div>
        </>
    );
}
